import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BookOpen, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const MOOD_TAGS = ["great", "good", "okay", "low", "struggling"];

export default function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [content, setContent] = useState("");
  const [mood, setMood] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gated, setGated] = useState(false);

  const load = async () => {
    try { const r = await api.get("/journal"); setEntries(r.data); setGated(false); }
    catch (e) { if (e.response?.status === 402) setGated(true); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try { await api.post("/journal", { content, mood }); setContent(""); setMood(null); toast.success("Saved privately"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      load();
    } catch (err) {
      console.warn("Delete journal entry failed", err);
    }
  };

  if (gated) {
    return (
      <div className="brutal-card bg-accent p-8 text-center" data-testid="journal-gate">
        <Sparkles size={28} strokeWidth={2.5} className="mx-auto mb-2" />
        <h2 className="text-3xl">Journal is part of HAEVEN Calm</h2>
        <p className="mt-2 text-sm">Private, encrypted entries with mood tags and weekly AI reflections.</p>
        <Link to="/premium" className="brutal-btn bg-primary text-primary-foreground inline-block mt-5 px-5 py-2" data-testid="upgrade-btn">See plans</Link>
      </div>
    );
  }

  return (
    <div data-testid="journal-page">
      <div className="flex items-center gap-2 mb-3"><BookOpen size={26} strokeWidth={2.5} /> <h1 className="text-4xl">Journal</h1></div>
      <p className="text-muted-foreground mb-5">A private space. Nobody else sees this — not even our care AI unless you ask for a weekly report.</p>

      <div className="brutal-card bg-card p-5 mb-6">
        <textarea data-testid="journal-textarea" value={content} onChange={(e) => setContent(e.target.value)}
          rows={4} placeholder="What happened? How did it feel?" className="brutal-input w-full px-4 py-3" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase font-semibold tracking-wider">Mood:</span>
          {MOOD_TAGS.map((m) => (
            <button key={m} type="button" onClick={() => setMood(mood === m ? null : m)} data-testid={`j-mood-${m}`}
              className={`brutal-btn px-3 py-1 text-xs ${mood === m ? "bg-foreground text-background" : "bg-card"}`}>{m}</button>
          ))}
          <div className="flex-1" />
          <button onClick={save} disabled={busy || !content.trim()} className="brutal-btn bg-primary text-primary-foreground px-4 py-2 disabled:opacity-60" data-testid="save-journal-btn">
            <Plus size={14} strokeWidth={3} className="inline mr-1" /> Save entry
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {entries.length === 0 ? <div className="brutal-card bg-card p-6 text-sm text-muted-foreground">No entries yet. Pour something in. ☕</div>
          : entries.map((e) => (
            <div key={e.id} className="brutal-card bg-card p-5" data-testid={`journal-entry-${e.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground mb-1">{new Date(e.created_at).toLocaleString()} {e.mood && <>· <span className="font-semibold uppercase tracking-wider">{e.mood}</span></>}</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{e.content}</p>
                </div>
                <button onClick={() => remove(e.id)} className="brutal-btn bg-card p-1" data-testid={`delete-journal-${e.id}`}><X size={14} strokeWidth={2.5} /></button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
